import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInvitationActions, useInvitationData } from '@/contexts/invitation-context';
import { usePrepareInvitation } from '@/hooks/actions';
import { useEmailIntegrationStatus } from '@/hooks/use-email-integration-status';
import type { InvitationChannelMode } from '@/stores/invitation-store';
import { BulkEmailNote, partitionByEmail } from './components';
import {
  bestAvailableChannel,
  isChannelSelectable,
  type InvitationChannelCapabilities,
} from './invitation-channel-helpers';
import { InvitationChannelPicker } from './invitation-channel-picker';

interface InvitationChannelStepProps {
  onClose: ({ success }: { success?: boolean }) => void;
}

/**
 * Channel step of the affiliation flow, between the created invitation (mono) or
 * the selected program (bulk) and delivery. Explicit radios replace the old
 * implicit routing. The shareable link is always offered; email is gated on a
 * usable mailbox; DM and comment come from the prepare capabilities (mono only).
 *
 * Bulk symmetry: the same step serves the wishlist bulk flow with only link and
 * email (no social profile, so no DM/comment). It shows the "X of N have an
 * email" counter; the mail-less warning only rides the EMAIL choice — in link
 * mode every creator is invitable (mail-less ones anchor on their social
 * profile server-side), so the link channel is the universal floor. Continue
 * only commits the chosen mode and moves on; no send happens here.
 */
const InvitationChannelStep = ({ onClose }: InvitationChannelStepProps) => {
  const { t } = useTranslation();
  const { invitationData, target, recipientEmail, invitationChannelMode } = useInvitationData();
  const { setInvitationChannelMode, setCurrentView } = useInvitationActions();

  const { hasUsableMailbox, isLoading: isIntegrationLoading } = useEmailIntegrationStatus();

  const isBulk = target?.type === 'wishlist-bulk';
  const bulkPartition = useMemo(
    () => partitionByEmail(isBulk ? target.data : []),
    [isBulk, target]
  );

  // The brand knows an email when the store carries one, the target itself is an
  // email / affiliate (both created by email), or — for bulk — at least one
  // selected item resolved an email.
  const emailAvailable = isBulk
    ? bulkPartition.emailCount > 0
    : !!recipientEmail || target?.type === 'email' || target?.type === 'affiliate';

  // Prepare (DM/comment capabilities) only exists for a mono social profile.
  // Email/affiliate/bulk targets have none, so it is never fetched.
  const socialProfileId = invitationData?.socialProfileId ?? null;
  const {
    data: prepare,
    isFetching: isPreparing,
    isError: prepareError,
  } = usePrepareInvitation(invitationData?.invitationId ?? '', {
    enabled: !!socialProfileId,
  });

  const capabilities: InvitationChannelCapabilities = {
    emailAvailable,
    emailEnabled: emailAvailable && hasUsableMailbox,
    // Bulk never offers DM/comment (no social profile). Mono reads prepare; a
    // prepare error is never blocking (fall back to no DM/comment, link floor).
    dmAvailable: !isBulk && !prepareError && (prepare?.canSendDirectMessage ?? false),
    commentAvailable: !isBulk && !prepareError && (prepare?.canComment ?? false),
    emailStatusPending: isIntegrationLoading,
  };

  // Explicit user choice wins while it stays selectable (a return via "Change"
  // restores the stored mode); otherwise the best available channel is
  // preselected (email > dm > comment > link).
  const [selected, setSelected] = useState<InvitationChannelMode | null>(invitationChannelMode);
  const mode: InvitationChannelMode =
    selected && isChannelSelectable(selected, capabilities)
      ? selected
      : bestAvailableChannel(capabilities);

  // Only truly in-flight while a gated mono prepare is fetching: the CTA waits so
  // DM and comment rows never flash in after the choice was made.
  const isResolving = !!socialProfileId && isPreparing;

  const handleContinue = () => {
    setInvitationChannelMode(mode);
    setCurrentView('invitationDetails');
  };

  return (
    <Flex direction="column" gap="3">
      <Box>
        <Text
          fontFamily="heading"
          fontSize="10.5px"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.06em"
          color="primary.500"
          mb="2"
        >
          {t('invitations.modal.channel.picker.title')}
        </Text>
        <InvitationChannelPicker value={mode} onChange={setSelected} capabilities={capabilities} />
      </Box>

      {isBulk && (
        <BulkEmailNote partition={bulkPartition} showCounter showWarning={mode === 'email'} />
      )}

      <HStack justify="flex-end" gap="2" pt="1">
        <Button variant="outline" onClick={() => onClose({ success: false })}>
          {t('common.cancel')}
        </Button>
        <Button colorPalette="primary" onClick={handleContinue} loading={isResolving}>
          {t('invitations.modal.collab.continue')}
        </Button>
      </HStack>
    </Flex>
  );
};

export default InvitationChannelStep;
